<template>
  <v-container class="payment-import py-7">
    <div class="payment-import__header">
      <div>
        <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="breadcrumbs" />
        <h1 class="payment-import__title">Importação em lote</h1>
        <p class="payment-import__subtitle">Envie até 500 pagamentos de uma vez usando o modelo de planilha.</p>
      </div>
      <v-btn outlined color="primary" to="/comprovantes-pagamento">
        <v-icon left>mdi-arrow-left</v-icon>Voltar aos comprovantes
      </v-btn>
    </div>

    <v-card outlined class="payment-import__stepper mb-5">
      <div v-for="(step, index) in steps" :key="step.label" class="payment-import__step">
        <div class="payment-import__step-mark" :class="{
          'payment-import__step-mark--active': index === currentStep,
          'payment-import__step-mark--done': index < currentStep
        }">
          <v-icon v-if="index < currentStep" small color="white">mdi-check</v-icon>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span :class="{ 'primary--text font-weight-bold': index === currentStep }">{{ step.label }}</span>
        <div v-if="index < steps.length - 1" class="payment-import__step-line"
          :class="{ 'payment-import__step-line--done': index < currentStep }" />
      </div>
    </v-card>

    <v-row v-if="!currentImport">
      <v-col cols="12" lg="8">
        <v-card outlined class="payment-import__card">
          <v-card-title class="payment-import__card-title">Prepare e envie sua planilha</v-card-title>
          <v-card-text>
            <div class="payment-import__guide-title">
              <span>1</span>
              <div>
                <strong>Baixe o modelo oficial</strong>
                <p>O arquivo já contém as colunas, instruções e uma linha de exemplo.</p>
              </div>
            </div>
            <div class="payment-import__template">
              <div class="d-flex align-center">
                <v-avatar color="#EEF1FB" size="48" class="mr-4">
                  <v-icon color="primary">mdi-file-excel-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">Modelo oficial de importação</div>
                  <div class="payment-import__muted">Já contém os nomes das colunas e uma linha de exemplo.</div>
                </div>
              </div>
              <v-btn color="primary" outlined :loading="downloadingTemplate" @click="downloadTemplate">
                <v-icon left>mdi-download</v-icon>Baixar modelo
              </v-btn>
            </div>

            <div class="payment-import__guide-title mt-6">
              <span>2</span>
              <div>
                <strong>Envie a planilha preenchida</strong>
                <p>Antes de continuar, confira se os documentos estão acessíveis pelos links informados.</p>
              </div>
            </div>
            <div class="payment-import__dropzone mt-5" :class="{ 'payment-import__dropzone--selected': spreadsheet }">
              <div class="payment-import__dropzone-content">
                <v-icon v-if="spreadsheet" size="42" color="primary">mdi-file-check-outline</v-icon>
                <v-icon v-else size="42" color="primary">mdi-cloud-upload-outline</v-icon>
                <div v-if="spreadsheet" class="font-weight-bold mt-3">{{ spreadsheet.name }}</div>
                <div v-else class="font-weight-bold mt-3">Arraste a planilha aqui ou clique para selecionar</div>
                <div class="payment-import__muted mt-1">
                  {{ spreadsheet ? "Planilha pronta para validação" : "Arquivo XLSX ou XLS, com até 500 pagamentos" }}
                </div>
              </div>
              <v-file-input class="payment-import__file-input" accept=".xlsx,.xls"
                prepend-icon="" hide-details label="Selecionar planilha"
                v-model="spreadsheet" :disabled="uploading" />
            </div>

            <div class="payment-import__checklist">
              <strong>Antes de enviar, confira:</strong>
              <div class="payment-import__checklist-grid">
                <div v-for="instruction in instructions" :key="instruction.title">
                  <v-icon color="success" small>mdi-check-circle</v-icon>
                  <span><strong>{{ instruction.title }}</strong><small>{{ instruction.text }}</small></span>
                </div>
              </div>
              <v-alert color="#FFF7E8" border="left" colored-border class="mt-4 mb-0">
                Os documentos precisam estar no Google Drive com acesso
                <strong>“Qualquer pessoa com o link”</strong>.
              </v-alert>
            </div>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer />
            <v-btn color="primary" elevation="0" :loading="uploading" :disabled="!spreadsheet"
              @click="uploadSpreadsheet">
              Validar planilha<v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card outlined class="payment-import__card payment-import__history-card">
          <v-card-title class="payment-import__card-title">
            Importações anteriores
          </v-card-title>
          <v-card-text>
            <v-skeleton-loader v-if="loadingImports" type="list-item-three-line@3" />
            <div v-else-if="imports.length" class="payment-import__history-list">
              <button v-for="item in imports.slice(0, 5)" :key="item.id" type="button"
                @click="openImport(item.id)">
                <span class="payment-import__history-icon"><v-icon color="primary" small>mdi-file-excel-outline</v-icon></span>
                <span>
                  <strong>{{ item.source_file_name }}</strong>
                  <small>{{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</small>
                </span>
                <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
              </button>
            </div>
            <div v-else class="payment-import__history-empty">
              <v-icon color="#98A2B3" size="34">mdi-file-clock-outline</v-icon>
              <strong>Nenhuma importação anterior</strong>
              <span>Os envios realizados aparecerão aqui.</span>
            </div>
            <div class="payment-import__history-help">
              <v-icon color="primary" small>mdi-information-outline</v-icon>
              <div>
                <strong>Limite de 500 pagamentos</strong>
                <span>A validação acontece antes da confirmação definitiva.</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="!currentImport" outlined
      class="payment-import__card payment-import__instructions-card mb-5">
      <v-card-title class="payment-import__card-title">
        <v-icon color="primary" left>mdi-book-open-page-variant-outline</v-icon>
        Como preparar a planilha
      </v-card-title>
      <v-card-text>
        <p class="payment-import__instructions-intro">
          Baixe o modelo oficial e preencha somente a aba <strong>Comprovantes</strong>.
          Os nomes apresentados abaixo são os mesmos encontrados na planilha.
        </p>

        <div class="payment-import__preparation-steps">
          <div v-for="(item, index) in preparationSteps" :key="item.title">
            <span>{{ index + 1 }}</span>
            <div>
              <strong>{{ item.title }}</strong>
              <small>{{ item.text }}</small>
            </div>
          </div>
        </div>

        <v-expansion-panels accordion flat class="payment-import__instructions-panels">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <strong>Aba Comprovantes</strong>
                <small>Uma linha para cada pagamento realizado.</small>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="payment-import__columns">
                <div v-for="column in spreadsheetColumns" :key="column.name">
                  <div>
                    <strong>{{ column.name }}</strong>
                    <v-chip x-small label :color="column.required ? '#E8ECFB' : '#F2F4F7'"
                      :text-color="column.required ? '#2A3070' : '#667085'">
                      {{ column.required ? "Obrigatório" : column.conditional ? "Condicional" : "Opcional" }}
                    </v-chip>
                  </div>
                  <p>{{ column.description }}</p>
                  <small v-if="column.accepted"><strong>Preenchimento:</strong> {{ column.accepted }}</small>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <strong>Documentos no Google Drive</strong>
                <small>Regras para que os arquivos possam ser baixados durante a importação.</small>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="payment-import__document-rules">
                <div v-for="rule in documentRules" :key="rule">
                  <v-icon color="success" small>mdi-check-circle</v-icon>
                  <span>{{ rule }}</span>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <v-card v-if="currentImport" outlined class="payment-import__card mb-5">
      <v-card-title class="payment-import__review-header">
        <div>
          <div class="payment-import__card-title pa-0">Conferência da importação</div>
          <div class="payment-import__muted mt-1">{{ currentImport.source_file_name }}</div>
        </div>
        <v-chip small :color="statusColor(currentImport.status)" dark>{{ statusLabel(currentImport.status) }}</v-chip>
      </v-card-title>

      <v-card-text>
        <div class="payment-import__summary">
          <div v-for="summary in importSummary" :key="summary.label" class="payment-import__summary-item">
            <span class="payment-import__muted">{{ summary.label }}</span>
            <strong :class="summary.color + '--text'">{{ summary.value }}</strong>
          </div>
        </div>

        <v-tabs v-model="reviewTab" background-color="transparent" color="primary" class="mt-5">
          <v-tab href="#invalid">Inválidos ({{ invalidItems.length }})</v-tab>
          <v-tab href="#duplicate">Duplicidades ({{ duplicateItems.length }})</v-tab>
          <v-tab href="#valid">Válidos ({{ validItems.length }})</v-tab>
        </v-tabs>

        <v-data-table :headers="headers" :items="reviewItems" :items-per-page="10"
          class="app-table payment-import__table" no-data-text="Nenhum registro nesta situação">
          <template v-slot:[`item.creditor`]="{ item }">
            <strong>{{ formatRegistration(item.normalized_payload.creditor_registration) }}</strong>
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            {{ formatCurrency(item.normalized_payload.paid_amount) }}
          </template>
          <template v-slot:[`item.documents`]="{ item }">
            {{ item.documents.length }} {{ item.documents.length === 1 ? "arquivo" : "arquivos" }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          </template>
          <template v-slot:[`item.errors`]="{ item }">
            <span :class="item.status === 'duplicate' ? 'warning--text' : 'error--text'">
              {{ itemError(item) || itemStatusDescription(item) }}
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn v-if="item.status === 'failed'" x-small outlined color="primary"
              :loading="retryingItemId === item.id" @click="retryItem(item)">Tentar novamente</v-btn>
          </template>
        </v-data-table>

        <v-alert v-if="duplicateItems.length && currentImport.status === 'awaiting_confirmation'"
          color="#FFF7E8" border="left" colored-border class="mt-4">
          <v-checkbox class="mt-0" hide-details v-model="confirmDuplicates"
            label="Importar também os pagamentos marcados como possível duplicidade" />
          <div class="payment-import__muted ml-8 mt-1">Se desmarcado, esses registros não serão importados.</div>
        </v-alert>
      </v-card-text>

      <v-progress-linear v-if="processing || currentImport.status === 'processing'"
        :value="progressPercent" height="10" color="primary" />

      <v-card-actions class="px-6 pb-6">
        <v-btn text color="primary" @click="resetImport">Enviar outra planilha</v-btn>
        <v-btn v-if="canDownloadReport" outlined color="primary" @click="downloadReport">
          <v-icon left>mdi-file-excel-outline</v-icon>Baixar resultado
        </v-btn>
        <v-spacer />
        <v-btn v-if="currentImport.status === 'awaiting_confirmation'" color="primary" elevation="0"
          :loading="confirming" @click="confirmImport">
          Importar {{ confirmableItems }} pagamentos
        </v-btn>
        <v-btn v-if="currentImport.status === 'processing'" color="primary" elevation="0"
          :loading="processing" @click="startProcessing">
          {{ processing ? "Processando..." : "Continuar processamento" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="currentImport" outlined class="payment-import__card">
      <v-card-title class="payment-import__card-title">Importações anteriores</v-card-title>
      <v-card-text>
        <v-data-table :headers="historyHeaders" :items="imports" :loading="loadingImports"
          :items-per-page="10" class="app-table" no-data-text="Nenhuma importação encontrada">
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          </template>
          <template v-slot:[`item.progress`]="{ item }">
            {{ Number(item.processed_items || 0) + Number(item.inconsistent_items || 0) }} de {{ item.total_items }}
          </template>
          <template v-slot:[`item.created_at`]="{ item }">
            {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn x-small outlined color="primary" @click="openImport(item.id)">Consultar</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "PaymentProofImport",
  data: () => ({
    breadcrumbs: [
      { text: "Comprovantes de pagamento", to: "/comprovantes-pagamento", exact: true },
      { text: "Importação em lote", disabled: true },
    ],
    spreadsheet: null,
    uploading: false,
    downloadingTemplate: false,
    confirming: false,
    processing: false,
    retryingItemId: null,
    confirmDuplicates: false,
    reviewTab: "invalid",
    currentImport: null,
    imports: [],
    loadingImports: false,
    steps: [
      { label: "Modelo e planilha" },
      { label: "Validação" },
      { label: "Conferência" },
      { label: "Processamento" },
    ],
    instructions: [
      { title: "Uma linha por pagamento", text: "A planilha aceita até 500 pagamentos por envio." },
      { title: "Preenchimento em português", text: "Use as opções descritas na aba Instruções, sem códigos técnicos." },
      { title: "Até cinco documentos", text: "Separe os links do Google Drive usando ponto e vírgula (;)." },
      { title: "Conferência antes de importar", text: "Inválidos e possíveis duplicidades serão destacados." },
    ],
    preparationSteps: [
      { title: "Baixe o modelo oficial", text: "Não altere o nome da aba nem os nomes das colunas." },
      { title: "Substitua a linha de exemplo", text: "Remova os dados de exemplo antes de incluir os pagamentos reais." },
      { title: "Informe um pagamento por linha", text: "Cada linha pode possuir de um a cinco documentos relacionados." },
      { title: "Revise e envie", text: "Confira os valores, as datas e o acesso aos links antes da validação." },
    ],
    spreadsheetColumns: [
      {
        name: "Identificador do pagamento",
        required: false,
        description: "Referência curta criada por você para localizar o pagamento e ajudar na conferência de duplicidades.",
        accepted: "Exemplo: PG-0001.",
      },
      {
        name: "CPF/CNPJ do credor",
        required: true,
        description: "Documento do credor que recebeu o pagamento, com ou sem pontuação.",
        accepted: "CPF ou CNPJ válido.",
      },
      {
        name: "Valor pago",
        required: true,
        description: "Valor efetivamente pago ao credor, sempre maior que zero.",
        accepted: "Exemplo: 1250,00. Não inclua o símbolo R$.",
      },
      {
        name: "Data do pagamento",
        required: true,
        description: "Data em que o pagamento foi realizado.",
        accepted: "Dia/mês/ano. Exemplo: 27/07/2026.",
      },
      {
        name: "Forma ou motivo do pagamento",
        required: true,
        description: "Selecione a opção que melhor representa o pagamento.",
        accepted: "Pix, Depósito em conta, Bloqueio judicial, Alvará judicial, Acordo ou Outro motivo.",
      },
      {
        name: "Tipo de pagador",
        required: true,
        description: "Indique quem realizou o pagamento.",
        accepted: "Próprio credor, Recuperanda, Sócio, Outra empresa ou Outro terceiro.",
      },
      {
        name: "Responsável pelo pagamento",
        required: true,
        description: "Nome da pessoa ou empresa responsável pelo pagamento.",
        accepted: "Nome completo ou razão social.",
      },
      {
        name: "CPF/CNPJ do pagador",
        required: false,
        conditional: true,
        description: "Necessário quando o pagamento não tiver sido realizado pelo próprio credor.",
        accepted: "CPF ou CNPJ válido, com ou sem pontuação.",
      },
      {
        name: "Processo relacionado",
        required: false,
        description: "Número do processo relacionado ao pagamento, quando houver.",
        accepted: "Exemplo: 0000000-00.0000.0.00.0000.",
      },
      {
        name: "Tipo de documento",
        required: true,
        description: "Informe qual documento comprova o pagamento.",
        accepted: "Comprovante de depósito, Comprovante de bloqueio, Alvará judicial ou Outro documento.",
      },
      {
        name: "Links dos documentos",
        required: true,
        description: "Links de compartilhamento dos arquivos que comprovam o pagamento.",
        accepted: "De um a cinco links do Google Drive, separados por ponto e vírgula (;).",
      },
    ],
    documentRules: [
      'Configure cada arquivo como “Qualquer pessoa com o link”.',
      "Utilize somente links de compartilhamento do Google Drive.",
      "Separe vários links com ponto e vírgula (;), sem utilizar vírgula.",
      "São permitidos até cinco documentos por pagamento e até 20 MB por documento.",
    ],
    headers: [
      { text: "Linha", value: "row_number" },
      { text: "CPF/CNPJ", value: "creditor" },
      { text: "Valor", value: "amount" },
      { text: "Documentos", value: "documents" },
      { text: "Status", value: "status" },
      { text: "Erros", value: "errors" },
      { text: "Ações", value: "actions", sortable: false },
    ],
    historyHeaders: [
      { text: "Arquivo", value: "source_file_name" },
      { text: "Status", value: "status" },
      { text: "Total", value: "total_items" },
      { text: "Progresso", value: "progress" },
      { text: "Criado em", value: "created_at" },
      { text: "Ações", value: "actions", sortable: false },
    ],
  }),
  computed: {
    currentStep() {
      if (!this.currentImport) return this.uploading ? 1 : 0;
      if (this.currentImport.status === "awaiting_confirmation") return 2;
      if (["processing", "completed", "completed_with_errors"].includes(this.currentImport.status)) return 3;
      return 1;
    },
    invalidItems() {
      return this.currentImport && this.currentImport.items
        ? this.currentImport.items.filter((item) => ["invalid", "failed"].includes(item.status))
        : [];
    },
    duplicateItems() {
      return this.currentImport && this.currentImport.items
        ? this.currentImport.items.filter((item) => item.status === "duplicate")
        : [];
    },
    validItems() {
      return this.currentImport && this.currentImport.items
        ? this.currentImport.items.filter((item) =>
          ["ready", "processed", "inconsistent", "processing"].includes(item.status))
        : [];
    },
    reviewItems() {
      if (this.reviewTab === "duplicate") return this.duplicateItems;
      if (this.reviewTab === "valid") return this.validItems;
      return this.invalidItems;
    },
    confirmableItems() {
      return this.validItems.filter((item) => item.status === "ready").length +
        (this.confirmDuplicates ? this.duplicateItems.length : 0);
    },
    importSummary() {
      if (!this.currentImport) return [];
      return [
        { label: "Total", value: this.currentImport.total_items || 0, color: "primary" },
        { label: "Válidos", value: this.validItems.length, color: "success" },
        { label: "Inválidos", value: this.invalidItems.length, color: "error" },
        { label: "Duplicidades", value: this.duplicateItems.length, color: "warning" },
        {
          label: "Processados",
          value: Number(this.currentImport.processed_items || 0) +
            Number(this.currentImport.inconsistent_items || 0),
          color: "primary",
        },
      ];
    },
    progressPercent() {
      if (!this.currentImport || !this.currentImport.valid_items) return 0;
      if (["completed", "completed_with_errors"].includes(this.currentImport.status)) return 100;
      const complete = Number(this.currentImport.processed_items || 0) +
        Number(this.currentImport.inconsistent_items || 0) +
        Number(this.currentImport.failed_items || 0);
      return Math.min(100, (complete / Number(this.currentImport.valid_items)) * 100);
    },
    canDownloadReport() {
      return this.currentImport &&
        ["completed", "completed_with_errors"].includes(this.currentImport.status);
    },
  },
  async created() {
    await this.loadImports();
    if (this.$route.query.id) await this.openImport(this.$route.query.id);
  },
  methods: {
    saveBlob(response, name) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async downloadTemplate() {
      this.downloadingTemplate = true;
      try {
        const response = await axios.get("/api/client/payment-proof-imports/template", { responseType: "blob" });
        this.saveBlob(response, "modelo-importacao-comprovantes.xlsx");
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.downloadingTemplate = false;
      }
    },
    async uploadSpreadsheet() {
      this.uploading = true;
      try {
        const payload = new FormData();
        payload.append("file", this.spreadsheet);
        const response = await axios.post("/api/client/payment-proof-imports", payload);
        this.currentImport = response.data.content;
        this.confirmDuplicates = false;
        this.reviewTab = this.invalidItems.length ? "invalid" : this.duplicateItems.length ? "duplicate" : "valid";
        await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
      } finally {
        this.uploading = false;
      }
    },
    async loadImports() {
      this.loadingImports = true;
      try {
        const response = await axios.get("/api/client/payment-proof-imports", { params: { page: 1, limit: 50 } });
        const content = response.data.content || {};
        this.imports = content.data || [];
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.loadingImports = false;
      }
    },
    async openImport(id) {
      try {
        const response = await axios.get(`/api/client/payment-proof-imports/${id}`);
        this.currentImport = response.data.content;
        this.confirmDuplicates = false;
        this.reviewTab = this.invalidItems.length ? "invalid" : this.duplicateItems.length ? "duplicate" : "valid";
        if (this.currentImport.status === "processing") await this.startProcessing();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      }
    },
    async confirmImport() {
      this.confirming = true;
      try {
        const response = await axios.post(`/api/client/payment-proof-imports/${this.currentImport.id}/confirm`, {
          confirmed_duplicate_item_ids: this.confirmDuplicates
            ? this.duplicateItems.map((item) => item.id)
            : [],
        });
        this.currentImport = response.data.content;
        await this.startProcessing();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.confirming = false;
      }
    },
    async processingWorker() {
      while (this.processing) {
        const response = await axios.post(`/api/client/payment-proof-imports/${this.currentImport.id}/process-next`);
        const content = response.data.content;
        if (content.progress) {
          this.currentImport = { ...this.currentImport, ...content.progress };
        }
        if (!content.has_more) break;
      }
    },
    async startProcessing() {
      if (this.processing || !this.currentImport) return;
      this.processing = true;
      try {
        await Promise.all([this.processingWorker(), this.processingWorker(), this.processingWorker()]);
        await this.openImportWithoutProcessing(this.currentImport.id);
        await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
      } finally {
        this.processing = false;
      }
    },
    async openImportWithoutProcessing(id) {
      const response = await axios.get(`/api/client/payment-proof-imports/${id}`);
      this.currentImport = response.data.content;
    },
    async retryItem(item) {
      this.retryingItemId = item.id;
      try {
        const response = await axios.post(
          `/api/client/payment-proof-imports/${this.currentImport.id}/items/${item.id}/retry`
        );
        this.currentImport = response.data.content;
        if (this.currentImport.status === "processing") await this.startProcessing();
        else await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.retryingItemId = null;
      }
    },
    async downloadReport() {
      try {
        const response = await axios.get(
          `/api/client/payment-proof-imports/${this.currentImport.id}/report`,
          { responseType: "blob" }
        );
        this.saveBlob(response, `resultado-importacao-${this.currentImport.id}.xlsx`);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      }
    },
    itemError(item) {
      const validation = item.validation_errors || [];
      const documents = (item.documents || []).map((document) => document.error_message).filter(Boolean);
      return [...validation, ...documents].join(" | ");
    },
    itemStatusDescription(item) {
      if (item.status === "duplicate") {
        return "Já existe um pagamento com os mesmos dados. Confira antes de importar.";
      }
      if (item.status === "inconsistent") {
        return "Importado com informação que precisa ser conferida pela Administração Judicial.";
      }
      return "";
    },
    resetImport() {
      this.currentImport = null;
      this.spreadsheet = null;
      this.confirmDuplicates = false;
      this.reviewTab = "invalid";
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
    statusLabel(status) {
      return {
        validating: "Validando",
        awaiting_confirmation: "Aguardando confirmação",
        processing: "Processando",
        completed: "Concluído",
        completed_with_errors: "Concluído com erros",
        invalid: "Inválido",
        ready: "Pronto",
        processed: "Processado",
        inconsistent: "Com inconsistência",
        failed: "Falhou",
        duplicate: "Possível duplicidade",
      }[status] || status;
    },
    statusColor(status) {
      if (["completed", "processed"].includes(status)) return "success";
      if (["failed", "invalid"].includes(status)) return "error";
      if (["completed_with_errors", "inconsistent", "duplicate"].includes(status)) return "warning";
      if (["processing", "validating"].includes(status)) return "info";
      return "primary";
    },
  },
};
</script>

<style scoped>
.payment-import {
  max-width: 1320px;
}

.payment-import__header,
.payment-import__template,
.payment-import__review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.payment-import__header {
  margin-bottom: 24px;
}

.payment-import__title {
  color: #101828;
  font-size: 28px;
  line-height: 1.25;
}

.payment-import__subtitle,
.payment-import__muted {
  color: #667085;
}

.payment-import__subtitle {
  margin: 6px 0 0;
}

.payment-import__card,
.payment-import__stepper {
  border-color: #e4e7ec !important;
  border-radius: 10px;
}

.payment-import__card-title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
}

.payment-import__stepper {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 20px 24px;
}

.payment-import__step {
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  color: #667085;
}

.payment-import__step > span {
  position: relative;
  z-index: 2;
  padding-right: 8px;
  background: #fff;
  font-size: 13px;
}

.payment-import__step-mark {
  z-index: 1;
  display: flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d0d5dd;
  border-radius: 50%;
  background: #fff;
  color: #98a2b3;
}

.payment-import__step-mark--active {
  border-color: #2a3070;
  background: #eef1fb;
  color: #2a3070;
  font-weight: 700;
}

.payment-import__step-mark--done {
  border-color: #2a3070;
  background: #2a3070;
}

.payment-import__step-line {
  position: absolute;
  z-index: 0;
  top: 15px;
  right: 0;
  left: 30px;
  height: 1px;
  background: #e4e7ec;
}

.payment-import__step-line--done {
  background: #2a3070;
}

.payment-import__dropzone {
  position: relative;
  display: flex;
  min-height: 190px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 1.5px dashed #98a2b3;
  border-radius: 10px;
  background: #fafbfc;
}

.payment-import__dropzone-content {
  position: relative;
  z-index: 1;
  text-align: center;
  pointer-events: none;
}

.payment-import__dropzone--selected {
  border-color: #2a3070;
  background: #f7f8fc;
}

.payment-import__guide-title {
  display: flex;
  align-items: flex-start;
  gap: 11px;
}

.payment-import__guide-title > span {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef1fb;
  color: #2a3070;
  font-size: 12px;
  font-weight: 700;
}

.payment-import__guide-title strong {
  color: #20263b;
  font-size: 14px;
}

.payment-import__guide-title p {
  margin: 2px 0 0;
  color: #667085;
  font-size: 12px;
}

.payment-import__checklist {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fbfcfe;
  color: #20263b;
  font-size: 13px;
}

.payment-import__checklist-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
  margin-top: 13px;
}

.payment-import__checklist-grid > div {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.payment-import__checklist-grid span {
  display: flex;
  flex-direction: column;
}

.payment-import__checklist-grid small {
  margin-top: 2px;
  color: #667085;
  line-height: 1.35;
}

.payment-import__history-card {
  height: 100%;
}

.payment-import__history-list {
  display: grid;
  gap: 7px;
}

.payment-import__history-list button {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  text-align: left;
}

.payment-import__history-list button:hover {
  border-color: #c7d0f2;
  background: #f8f9fd;
}

.payment-import__history-icon {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #eef1fb;
}

.payment-import__history-list button > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.payment-import__history-list button strong {
  overflow: hidden;
  color: #20263b;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-import__history-list button small {
  color: #667085;
  font-size: 10.5px;
}

.payment-import__history-empty {
  display: flex;
  min-height: 190px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: #667085;
  text-align: center;
}

.payment-import__history-empty strong {
  color: #344054;
  font-size: 13px;
}

.payment-import__history-empty span {
  font-size: 12px;
}

.payment-import__history-help {
  display: flex;
  gap: 9px;
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: #f4f6fb;
}

.payment-import__history-help > div {
  display: flex;
  flex-direction: column;
  color: #344054;
  font-size: 12px;
}

.payment-import__history-help span {
  margin-top: 2px;
  color: #667085;
  font-size: 11px;
}

.payment-import__instructions-intro {
  max-width: 900px;
  margin: 0 0 20px;
  color: #667085;
  line-height: 1.55;
}

.payment-import__preparation-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.payment-import__preparation-steps > div {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fafbfc;
}

.payment-import__preparation-steps > div > span {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  border-radius: 50%;
  background: #eef1fb;
  color: #2a3070;
  font-weight: 700;
}

.payment-import__preparation-steps strong,
.payment-import__preparation-steps small,
.payment-import__instructions-panels small {
  display: block;
}

.payment-import__preparation-steps small,
.payment-import__instructions-panels small {
  margin-top: 3px;
  color: #667085;
}

.payment-import__instructions-panels {
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.payment-import__instructions-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.payment-import__instructions-panels ::v-deep .v-expansion-panel + .v-expansion-panel {
  border-top: 1px solid #e4e7ec;
}

.payment-import__columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding-bottom: 8px;
}

.payment-import__columns > div {
  padding: 12px 14px;
  border: 1px solid #eaecf0;
  border-radius: 7px;
}

.payment-import__columns > div > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.payment-import__columns p {
  margin: 6px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.45;
}

.payment-import__columns > div > small {
  margin-top: 6px;
  color: #475467;
  font-size: 11.5px;
  line-height: 1.45;
}

.payment-import__document-rules {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
  padding-bottom: 8px;
}

.payment-import__document-rules > div {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #475467;
  font-size: 12px;
  line-height: 1.45;
}

.payment-import__file-input {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.payment-import__file-input ::v-deep .v-input__control,
.payment-import__file-input ::v-deep .v-input__slot {
  height: 100%;
}

.payment-import__file-input ::v-deep input {
  cursor: pointer;
}

.payment-import__instruction {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #eef0f3;
}

.payment-import__instruction:last-child {
  border-bottom: 0;
}

.payment-import__summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.payment-import__summary-item {
  display: flex;
  min-height: 76px;
  flex-direction: column;
  justify-content: center;
  padding: 12px 16px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.payment-import__summary-item strong {
  margin-top: 3px;
  font-size: 22px;
}

@media (max-width: 960px) {
  .payment-import__header,
  .payment-import__template,
  .payment-import__review-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .payment-import__stepper,
  .payment-import__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .payment-import__step-line {
    display: none;
  }

  .payment-import__checklist-grid {
    grid-template-columns: 1fr;
  }

  .payment-import__preparation-steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .payment-import__stepper,
  .payment-import__summary,
  .payment-import__preparation-steps,
  .payment-import__columns,
  .payment-import__document-rules {
    grid-template-columns: 1fr;
  }
}
</style>
